import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  hämtaKanalinkopare, hämtaAktiviteterFörKanalinkopare, skapaKanalaktivitet,
  hämtaViner, hämtaSäljare,
  Kanalinkopare, Kanalaktivitet, Kanalprovning, Vin, Saljare,
} from '../api/client';
import { TEMA } from '../theme';
import SokbarLista from '../components/SokbarLista';

// Återanvänder temats tre nyanser (fel-röd → guld → grön) som gradient
// för poängmätaren, istället för en fristående färgskala.
function skalaFärg(v: number): string {
  const röd: [number, number, number] = [217, 105, 95];
  const guld: [number, number, number] = [224, 168, 62];
  const grön: [number, number, number] = [95, 174, 138];
  let a = röd, b = guld, t = (v - 1) / 4.5;
  if (v > 5.5) { a = guld; b = grön; t = (v - 5.5) / 4.5; }
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `rgb(${r},${g},${bl})`;
}

function Poängmätare({ värde }: { värde: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <span style={{ display: 'inline-flex', gap: '2px' }}>
        {Array.from({ length: 10 }, (_, i) => i + 1).map(i => (
          <span
            key={i}
            style={{
              width: '6px', height: '6px', borderRadius: '50%', display: 'inline-block',
              background: i <= värde ? skalaFärg(värde) : TEMA.border,
            }}
          />
        ))}
      </span>
      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: TEMA.inkFaint }}>{värde}/10</span>
    </span>
  );
}

const sektionsLabel: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: '11px',
  fontWeight: 500,
  color: TEMA.accent,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  margin: '0 0 12px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  fontSize: '14px',
  fontFamily: "'Inter', sans-serif",
  borderRadius: '2px',
  border: `1px solid ${TEMA.border}`,
  background: TEMA.bg,
  color: TEMA.ink,
  boxSizing: 'border-box',
  outline: 'none',
};

const fältLabel: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: '11px',
  color: TEMA.inkFaint,
  margin: '0 0 4px',
};

const kortStyle: React.CSSProperties = {
  background: TEMA.bgPanel,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '8px',
  padding: '22px 24px',
  marginBottom: '16px',
};

const btnPrimärStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 500,
  padding: '9px 18px',
  borderRadius: '2px',
  border: `1px solid ${TEMA.accent}`,
  background: TEMA.accent,
  color: TEMA.onAccent,
  cursor: 'pointer',
};

const btnGhostStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  padding: '9px 18px',
  borderRadius: '2px',
  border: `1px solid ${TEMA.border}`,
  background: 'transparent',
  color: TEMA.inkSoft,
  cursor: 'pointer',
};

const tomProvning = { vin_namn: '', reaktion: 5, pris_accepterat: null as boolean | null, kommentar: '' };
const tomAktivitet = { typ: 'möte' as 'möte' | 'provinlämning', datum: '', saljare_id: '', notat: '' };

export default function KanalInkopareDetalj() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [inkopare, setInkopare] = useState<Kanalinkopare | null>(null);
  const [aktiviteter, setAktiviteter] = useState<Kanalaktivitet[]>([]);
  const [viner, setViner] = useState<Vin[]>([]);
  const [säljare, setSäljare] = useState<Saljare[]>([]);
  const [laddar, setLaddar] = useState(true);
  const [fel, setFel] = useState<string | null>(null);

  const [visaFormulär, setVisaFormulär] = useState(false);
  const [aktivitetUtkast, setAktivitetUtkast] = useState(tomAktivitet);
  const [provningsUtkast, setProvningsUtkast] = useState<typeof tomProvning[]>([{ ...tomProvning }]);
  const [sparar, setSparar] = useState(false);
  const [formulärFel, setFormulärFel] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    laddaAllt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function laddaAllt() {
    setLaddar(true);
    setFel(null);
    Promise.all([hämtaKanalinkopare(), hämtaAktiviteterFörKanalinkopare(id!), hämtaViner(), hämtaSäljare()])
      .then(([ik, akt, v, s]) => {
        setInkopare(ik.data.find(i => i.id === id) || null);
        setAktiviteter(akt.data);
        setViner(v.data);
        setSäljare(s.data);
        setLaddar(false);
      })
      .catch(err => {
        console.error('Kunde inte ladda kanalinköpare:', err);
        setFel('Kunde inte ladda sidan just nu. Försök igen om en stund.');
        setLaddar(false);
      });
  }

  function öppnaFormulär() {
    setAktivitetUtkast(tomAktivitet);
    setProvningsUtkast([{ ...tomProvning }]);
    setFormulärFel(null);
    setVisaFormulär(true);
  }

  function läggTillProvningsrad() {
    setProvningsUtkast(p => [...p, { ...tomProvning }]);
  }

  function taBortProvningsrad(index: number) {
    setProvningsUtkast(p => p.filter((_, i) => i !== index));
  }

  function uppdateraProvningsrad(index: number, ändring: Partial<typeof tomProvning>) {
    setProvningsUtkast(p => p.map((rad, i) => i === index ? { ...rad, ...ändring } : rad));
  }

  async function sparaAktivitet() {
    if (!id) return;
    if (!aktivitetUtkast.datum) {
      setFormulärFel('Datum krävs.');
      return;
    }
    const giltigaProvningar = provningsUtkast.filter(p => p.vin_namn.trim());
    setSparar(true);
    setFormulärFel(null);
    try {
      await skapaKanalaktivitet({
        kanalinkopare_id: id,
        typ: aktivitetUtkast.typ,
        datum: aktivitetUtkast.datum,
        saljare_id: aktivitetUtkast.saljare_id || null,
        notat: aktivitetUtkast.notat,
        provningar: giltigaProvningar as Kanalprovning[],
      });
      setVisaFormulär(false);
      laddaAllt();
    } catch (err) {
      console.error('Kunde inte spara aktivitet:', err);
      setFormulärFel('Kunde inte spara aktiviteten. Försök igen.');
    } finally {
      setSparar(false);
    }
  }

  if (laddar) return <p style={{ color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>Laddar...</p>;
  if (fel) return <p style={{ color: '#D9695F', fontFamily: "'Inter', sans-serif" }}>{fel}</p>;
  if (!inkopare) return <p style={{ color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>Kanalinköpare hittades inte.</p>;

  const säljareNamn = (saljare_id?: string | null) => säljare.find(s => s.id === saljare_id)?.namn || '—';

  return (
    <div>
      <button onClick={() => navigate('/anbud/aktiviteter')} style={{
        fontFamily: "'Inter', sans-serif",
        fontSize: '13px', color: TEMA.inkSoft, background: 'none', border: 'none',
        cursor: 'pointer', padding: 0, marginBottom: '20px',
      }}>
        ← Tillbaka
      </button>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '28px' }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: '24px', color: TEMA.ink, margin: 0 }}>{inkopare.namn}</h1>
        {inkopare.ansvarsområde && (
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: '11px', letterSpacing: '0.04em', padding: '3px 10px',
            borderRadius: '2px', border: `1px solid ${TEMA.border}`, color: TEMA.inkSoft,
          }}>
            {inkopare.ansvarsområde}
          </span>
        )}
      </div>

      <p style={sektionsLabel}>Kontakt</p>
      <div style={kortStyle}>
        <p style={{ margin: 0, fontSize: '14px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>
          {[inkopare.telefon, inkopare.email].filter(Boolean).join(' · ') || 'Ingen kontaktinfo registrerad'}
        </p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '32px', marginBottom: '16px' }}>
        <p style={{ ...sektionsLabel, margin: 0 }}>Aktivitetshistorik</p>
        {!visaFormulär && (
          <button onClick={öppnaFormulär} style={btnPrimärStyle}>
            + Registrera aktivitet
          </button>
        )}
      </div>

      {visaFormulär && (
        <div style={kortStyle}>
          {formulärFel && <p style={{ color: '#D9695F', fontSize: '13px', margin: '0 0 12px', fontFamily: "'Inter', sans-serif" }}>{formulärFel}</p>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div>
              <p style={fältLabel}>Typ</p>
              <select
                value={aktivitetUtkast.typ}
                onChange={e => setAktivitetUtkast(u => ({ ...u, typ: e.target.value as 'möte' | 'provinlämning' }))}
                style={inputStyle}
              >
                <option value="möte">Fysiskt möte</option>
                <option value="provinlämning">Provinlämning</option>
              </select>
            </div>
            <div>
              <p style={fältLabel}>Datum</p>
              <input
                type="date"
                value={aktivitetUtkast.datum}
                onChange={e => setAktivitetUtkast(u => ({ ...u, datum: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div>
              <p style={fältLabel}>Säljare</p>
              <select
                value={aktivitetUtkast.saljare_id}
                onChange={e => setAktivitetUtkast(u => ({ ...u, saljare_id: e.target.value }))}
                style={inputStyle}
              >
                <option value="">— Välj —</option>
                {säljare.map(s => <option key={s.id} value={s.id}>{s.namn}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <p style={fältLabel}>Notat</p>
            <textarea
              value={aktivitetUtkast.notat}
              onChange={e => setAktivitetUtkast(u => ({ ...u, notat: e.target.value }))}
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <p style={{ fontFamily: "'Fraunces', serif", fontSize: '15px', color: TEMA.ink, margin: '0 0 10px' }}>Provade/inskickade viner</p>
          {provningsUtkast.map((rad, index) => (
            <div key={index} style={{
              background: TEMA.bg, border: `1px solid ${TEMA.border}`, borderRadius: '6px',
              padding: '14px', marginBottom: '10px',
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px', marginBottom: '10px' }}>
                <div>
                  <p style={fältLabel}>Vin</p>
                  <SokbarLista
                    värde={viner.some(v => v.namn === rad.vin_namn) ? rad.vin_namn : ''}
                    onChange={namn => uppdateraProvningsrad(index, { vin_namn: namn })}
                    placeholder="Välj vin..."
                    alternativ={viner.map(v => ({
                      värde: v.namn,
                      etikett: v.namn,
                      under: v.producent || undefined,
                    }))}
                  />
                  <input
                    type="text"
                    placeholder="Eller skriv in ett off-list-vin"
                    value={viner.some(v => v.namn === rad.vin_namn) ? '' : rad.vin_namn}
                    onChange={e => uppdateraProvningsrad(index, { vin_namn: e.target.value })}
                    style={{ ...inputStyle, marginTop: '8px' }}
                  />
                </div>
                <div>
                  <p style={fältLabel}>Pris accepterat</p>
                  <select
                    value={rad.pris_accepterat === null ? 'vet_ej' : rad.pris_accepterat ? 'ja' : 'nej'}
                    onChange={e => {
                      const v = e.target.value;
                      uppdateraProvningsrad(index, { pris_accepterat: v === 'vet_ej' ? null : v === 'ja' });
                    }}
                    style={inputStyle}
                  >
                    <option value="vet_ej">Vet ej</option>
                    <option value="ja">Ja</option>
                    <option value="nej">Nej</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <p style={{ ...fältLabel, margin: 0 }}>Reaktion</p>
                  <Poängmätare värde={rad.reaktion} />
                </div>
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={rad.reaktion}
                  onChange={e => uppdateraProvningsrad(index, { reaktion: parseInt(e.target.value, 10) })}
                  style={{ width: '100%', accentColor: TEMA.accent }}
                />
              </div>

              <input
                type="text"
                placeholder="Kommentar (valfritt)"
                value={rad.kommentar}
                onChange={e => uppdateraProvningsrad(index, { kommentar: e.target.value })}
                style={{ ...inputStyle, marginBottom: provningsUtkast.length > 1 ? '10px' : 0 }}
              />

              {provningsUtkast.length > 1 && (
                <button onClick={() => taBortProvningsrad(index)} style={{ ...btnGhostStyle, fontSize: '11px', padding: '4px 10px' }}>
                  Ta bort vin
                </button>
              )}
            </div>
          ))}

          <button onClick={läggTillProvningsrad} style={{ ...btnGhostStyle, marginBottom: '20px' }}>
            + Lägg till vin
          </button>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={sparaAktivitet} disabled={sparar} style={{ ...btnPrimärStyle, opacity: sparar ? 0.6 : 1 }}>
              {sparar ? 'Sparar...' : 'Spara aktivitet'}
            </button>
            <button onClick={() => setVisaFormulär(false)} disabled={sparar} style={btnGhostStyle}>
              Avbryt
            </button>
          </div>
        </div>
      )}

      {aktiviteter.length === 0 && !visaFormulär && (
        <p style={{ color: TEMA.inkFaint, textAlign: 'center', marginTop: '32px', fontFamily: "'Inter', sans-serif" }}>
          Inga aktiviteter registrerade än.
        </p>
      )}

      {aktiviteter.map(akt => {
        const ärMöte = akt.typ === 'möte';
        const typFärg = ärMöte ? TEMA.positive : TEMA.accent;
        return (
          <div key={akt.id} style={kortStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '11px', letterSpacing: '0.04em', padding: '3px 10px',
                borderRadius: '2px', border: `1px solid ${typFärg}`, color: typFärg,
              }}>
                {ärMöte ? 'FYSISKT MÖTE' : 'PROVINLÄMNING'}
              </span>
              <span style={{ fontSize: '14px', color: TEMA.ink, fontFamily: "'Inter', sans-serif" }}>{akt.datum}</span>
              <span style={{ fontSize: '13px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>· {säljareNamn(akt.saljare_id)}</span>
            </div>
            {akt.notat && <p style={{ margin: '0 0 14px', fontSize: '14px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>{akt.notat}</p>}

            {(akt.provningar || []).map((p, idx) => (
              <div key={p.id || idx} style={{
                padding: '10px 0',
                borderTop: idx > 0 ? `1px solid ${TEMA.border}` : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontFamily: "'Fraunces', serif", fontSize: '15px', color: TEMA.ink }}>{p.vin_namn}</span>
                  <Poängmätare värde={p.reaktion} />
                </div>
                <p style={{ margin: 0, fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                  {p.pris_accepterat === null ? 'Pris: vet ej' : p.pris_accepterat ? 'Pris accepterat' : 'Pris ej accepterat'}
                </p>
                {p.kommentar && (
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: TEMA.inkSoft, fontStyle: 'italic', fontFamily: "'Inter', sans-serif" }}>
                    "{p.kommentar}"
                  </p>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}